import { useMutation } from '@tanstack/react-query';
import {
  Activity,
  BookOpen,
  CircleSlash,
  CopyPlus,
  ListFilter,
  Lock,
  LucideIcon,
  Pencil,
  ShoppingBasket,
  Trash2,
  Zap,
} from 'lucide-react';
import { ComponentType, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { IndexPath } from '@/Config/app/router/index.path';
import {
  ActionDto,
  AIActionUsageStatsDto,
  AppActionDto,
  PatchActionDto,
} from '@/Domain/api/Action/dto/Action.dto';
import { ActionBoxIcon } from '@/Domain/components/Action/ActionBox/ActionBoxIcon';
import { ActionHealthBadge } from '@/Domain/components/Action/ActionBox/ActionHealthBadge';
import { ActionLogsSheet } from '@/Domain/components/Action/ActionLogs/ActionLogs.sheet';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { ToolIcon } from '@/Domain/components/icons/Tool.icon';
import { ActionTypeEnum, AppActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { formatRelativeTime } from '@/Domain/helper/RelativeTime.helper';
import { useActionHealth } from '@/Domain/hooks/Action/useActionHealth.hook';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';

type Props = {
  dataTestId: string;
  isPreview?: boolean;
  action: ActionDto | AppActionDto;
  stats?: AIActionUsageStatsDto;
  onActive?: (isActive: boolean) => void;
  onEdit?: () => void;
  buttonLabel: string;
  onMouseEnterAction?: () => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  isDuplicating?: boolean;
  canDuplicate?: boolean;
};

const WHITE_LIST_ACTIONS = [
  ActionTypeEnum.HttpRequest,
  ActionTypeEnum.ShopifyRemoteMcp,
  ActionTypeEnum.RemoteMcp,
  ActionTypeEnum.KnowledgeBase,
  AppActionTypeEnum.Campaign,
  AppActionTypeEnum.Handoff,
];

const adaptLucide =
  (Icon: LucideIcon): React.ComponentType<any> =>
  (props: any) => <Icon {...props} />;

const BadgeTypeConfig: Record<
  ActionTypeEnum | AppActionTypeEnum,
  { Icon: ComponentType<any>; text: string } | null
> = {
  [AppActionTypeEnum.Campaign]: null,
  [AppActionTypeEnum.Handoff]: null,
  [ActionTypeEnum.ShopifyRemoteMcp]: {
    Icon: adaptLucide(ShoppingBasket),
    text: i18n.t('actions_page:badge.shopify_mcp'),
  },
  [ActionTypeEnum.RemoteMcp]: {
    Icon: McpIcon,
    text: i18n.t('actions_page:badge.remote_mcp'),
  },
  [ActionTypeEnum.HttpRequest]: {
    Icon: ToolIcon,
    text: i18n.t('actions_page:badge.http_request_action'),
  },
  [ActionTypeEnum.KnowledgeBase]: {
    Icon: adaptLucide(BookOpen),
    text: i18n.t('actions_page:badge.knowledge_base_action'),
  },
  [ActionTypeEnum.Custom]: null,
};

const getSuccessDotColor = (stats: AIActionUsageStatsDto): string => {
  if (stats.error_count > 0) return 'bg-red-500';
  if (stats.warning_count > 0) return 'bg-amber-500';
  return 'bg-emerald-500';
};

export const ActionBox = ({
  dataTestId,
  isPreview = false,
  action,
  stats,
  onMouseEnterAction,
  onActive,
  onEdit,
  onDelete,
  onDuplicate,
  isDuplicating = false,
  canDuplicate = true,
  buttonLabel,
}: Props) => {
  const { t } = useTranslation('actions_page');

  // Why an action is refused, not just that it is. The server computes each
  // named check; without this the user sees "Incomplete" and has to guess.
  const validationIssues = action.validation_issues ?? [];
  const validationIssueList = validationIssues.length ? (
    <>
      <p className="font-medium">{t('validation.title')}</p>
      <ul className="list-disc pl-4">
        {validationIssues.map((issue) => (
          <li key={issue}>{t(`validation.${issue}`, t('validation.fallback'))}</li>
        ))}
      </ul>
    </>
  ) : null;

  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();

  // App actions are plan-gated: Campaigns need lead_generation, Handoff needs human_handoff.
  const planDisabled =
    isSubscriptionFetched &&
    ((action.type === AppActionTypeEnum.Campaign && !isActivated('lead_generation')) ||
      (action.type === AppActionTypeEnum.Handoff && !isActivated('human_handoff.active')));
  // Only fetch when an upgrade pill could render — keeps the network/test cost flat.
  const { data: subscriptionData } = useGetSubscription({ enabled: planDisabled });
  // Upgrade label for the two gated app actions. lead_generation (Campaign /
  // Smart Data Gathering) is on Free and every modern tier — false only on legacy
  // `basic`/`legacy_lite`, whose upgrade path is Essential. Handoff unlocks at Basic.
  const planRequiredLabelKey: 'badge.upgrade_to_basic' | 'badge.upgrade_to_essential' | null =
    planDisabled
      ? action.type === AppActionTypeEnum.Handoff
        ? 'badge.upgrade_to_basic'
        : 'badge.upgrade_to_essential'
      : null;
  // Match the tier-based variant logic used by UpgradePlanButton: bold gradient
  // for Trial users (highest-visibility conversion ask), soft outlined for
  // anyone already paying.
  const upgradeButtonVariant =
    subscriptionData?.current_plan?.tier === TierEnum.TRIAL ||
    subscriptionData?.current_plan?.tier === TierEnum.FREE
      ? 'upgrade'
      : 'upgrade_subtle';
  const canManageAction = WHITE_LIST_ACTIONS.includes(action.type);
  const isAiAction = Object.values(ActionTypeEnum).includes(action.type as ActionTypeEnum);

  const badge = BadgeTypeConfig[action.type];

  const [logsOpen, setLogsOpen] = useState(false);
  const [logsOnlyIssues, setLogsOnlyIssues] = useState(false);
  // Shopify MCP usage is attachment-derived; it has no MessageToolCall rows to log.
  const supportsCallLogs = action.type !== ActionTypeEnum.ShopifyRemoteMcp;
  const canOpenLogs = action.id != null && supportsCallLogs;
  const openLogs = (onlyIssues: boolean) => {
    setLogsOnlyIssues(onlyIssues);
    setLogsOpen(true);
  };

  const { data: health } = useActionHealth(
    action.id,
    !isPreview && action.id != null && isAiAction,
  );
  const skippedConversations = health?.skipped_conversations ?? 0;
  const hasCalls = (stats?.total_calls ?? 0) > 0;

  const patchActionMutation = useMutation<ActionDto, undefined, PatchActionDto>({
    mutationKey: [QueryKey.actions.basic.patch],
  });
  const onUpdateIcon = (name: string, color: string) =>
    patchActionMutation.mutate({ icon: name, icon_color: color, id: action.id! });

  return (
    <div data-testid="action-box" className="h-full">
      <TooltipProvider>
        <div
          data-testid={dataTestId}
          className="flex h-full flex-col gap-5 rounded-2xl border border-border bg-card p-6"
          onMouseEnter={() => onMouseEnterAction && onMouseEnterAction()}
        >
          <header className="flex items-center gap-1 justify-between">
            <section className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2">
              <ActionBoxIcon
                icon={action.icon}
                color={action.icon_color}
                onChange={(name, color) => action.id && onUpdateIcon(name, color)}
                canSelectIcon={!isPreview && isAiAction}
              />
              {!action.is_valid &&
                (validationIssueList ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* A button, not a span: Badge is not a forwardRef component so
                          asChild needs a real element, and the trigger has to be
                          focusable or keyboard users can never read the reasons. */}
                      <button type="button" className="cursor-help rounded-sm">
                        <Badge variant="error" data-testid="action-box-incomplete-badge">
                          {t('incomplete')}
                        </Badge>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      className="max-w-xs"
                      data-testid="action-box-incomplete-reasons"
                    >
                      {validationIssueList}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <Badge variant="error" data-testid="action-box-incomplete-badge">
                    {t('incomplete')}
                  </Badge>
                ))}
              {health && (
                <ActionHealthBadge
                  health={health}
                  onOpenLogs={canOpenLogs ? () => openLogs(true) : undefined}
                />
              )}
            </section>

            <div className="flex items-start gap-2">
              {planDisabled && planRequiredLabelKey && (
                <Button
                  asChild
                  variant={upgradeButtonVariant}
                  size="xs"
                  data-testid="action-box-plan-locked-button"
                  className="!h-5 gap-1 whitespace-nowrap px-2 [&_svg]:!size-3"
                >
                  <Link to={`../${IndexPath.subscription}`}>
                    <Lock />
                    {t(planRequiredLabelKey)}
                  </Link>
                </Button>
              )}
              <Tooltip
                open={planDisabled || (!action.is_valid && !action.is_active) ? undefined : false}
              >
                <TooltipTrigger asChild>
                  {/* A disabled Switch is not focusable, so the wrapper takes the
                      focus instead — but only while there is a tooltip to read, to
                      avoid adding an empty tab stop. */}
                  <div
                    tabIndex={
                      planDisabled || (!action.is_valid && !action.is_active) ? 0 : undefined
                    }
                  >
                    <Switch
                      data-testid="action-box-activate-switch"
                      disabled={
                        !canManageAction || (!action.is_valid && !action.is_active) || planDisabled
                      }
                      checked={action.is_active}
                      onCheckedChange={
                        onActive ? (checked) => onActive(checked) : (_checked) => null
                      }
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  {planDisabled
                    ? t('toast.higher_plan_required')
                    : (validationIssueList ?? t('tooltip.action_invalid'))}
                </TooltipContent>
              </Tooltip>
            </div>
          </header>

          <section className="space-y-1.5">
            <p className="line-clamp-2 font-semibold text-foreground">
              {action.name || '[your_title]'}
            </p>
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {action.description || '[your_description]'}
            </p>
          </section>

          {badge && (
            <Badge variant="secondary" className="w-fit">
              <badge.Icon className="size-4 mr-2" /> {badge.text}
            </Badge>
          )}

          {isAiAction && (
            <div className="flex items-center justify-between gap-2">
              {stats && stats.total_calls > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="flex items-center gap-1.5 text-xs text-foreground-subtle font-medium"
                      data-testid="action-stats-badge"
                    >
                      <Zap className="size-3" />
                      <span>{t('stats.calls', { count: stats.total_calls })}</span>
                      <span>&middot;</span>
                      <span className="flex items-center gap-1">
                        <span
                          className={`inline-block size-1.5 rounded-full ${getSuccessDotColor(stats)}`}
                        />
                        {Math.round(stats.success_rate)}%
                      </span>
                      {stats.last_called_at && (
                        <>
                          <span>&middot;</span>
                          <span>{formatRelativeTime(stats.last_called_at)}</span>
                        </>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div>
                      {t('stats.tooltip', {
                        total: stats.total_calls,
                        success: stats.success_count,
                        warning: stats.warning_count,
                        error: stats.error_count,
                      })}
                    </div>
                    <div className="text-foreground-subtle">{t('stats.tooltip_window')}</div>
                  </TooltipContent>
                </Tooltip>
              ) : skippedConversations > 0 ? (
                <div
                  className="flex items-center gap-1.5 text-xs text-foreground-subtle font-medium"
                  data-testid="action-stats-skipped"
                >
                  <CircleSlash className="size-3" />
                  <span>{t('stats.skipped', { count: skippedConversations })}</span>
                </div>
              ) : (
                <div
                  className="flex items-center gap-1.5 text-xs text-foreground-subtle italic"
                  data-testid="action-stats-empty"
                >
                  <Activity className="size-3.5" />
                  <span>{t('stats.no_calls_yet')}</span>
                </div>
              )}
              {(hasCalls || skippedConversations > 0) && canOpenLogs && (
                <Button
                  size="xs"
                  variant="ghost"
                  className="shrink-0 text-foreground-subtle"
                  data-testid="action-box-view-logs-button"
                  onClick={() => openLogs(false)}
                >
                  <ListFilter className="size-4" />
                  {t('logs.view_logs_button')}
                </Button>
              )}
            </div>
          )}

          <Separator className="mt-auto" />

          <footer className="flex items-center justify-between ">
            <Button
              size="sm"
              data-testid="action-box-edit-button"
              variant="secondary"
              onClick={onEdit}
              disabled={!canManageAction}
            >
              <Pencil className="size-4" />
              {buttonLabel}
            </Button>
            <div className="flex items-center gap-2">
              {onDuplicate && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* A disabled Button takes neither hover nor focus; the wrapper does,
                        and tab-stops only when there is a cap to explain. */}
                    <div
                      data-testid="duplicate-action-tooltip-trigger"
                      tabIndex={canDuplicate ? undefined : 0}
                    >
                      <Button
                        data-testid="duplicate-action-button"
                        size="icon"
                        variant="secondary"
                        onClick={onDuplicate}
                        disabled={!canManageAction || !canDuplicate || isDuplicating}
                        aria-label={t('tooltip.duplicate_action')}
                      >
                        <CopyPlus className="size-4" />
                      </Button>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent data-testid="duplicate-action-tooltip">
                    {canDuplicate
                      ? t('tooltip.duplicate_action')
                      : t('tooltip.too_many_created_actions')}
                  </TooltipContent>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      data-testid="delete-action-button"
                      size="icon"
                      variant="secondary"
                      className="hover:!text-red-500"
                      onClick={onDelete}
                      disabled={!canManageAction}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('tooltip.delete_action')}</TooltipContent>
                </Tooltip>
              )}
            </div>
          </footer>

          {action.id != null && supportsCallLogs && (
            <ActionLogsSheet
              actionId={action.id}
              actionName={action.name}
              open={logsOpen}
              onOpenChange={setLogsOpen}
              initialOnlyIssues={logsOnlyIssues}
            />
          )}
        </div>
      </TooltipProvider>
    </div>
  );
};
